import React, { useState, useCallback } from "react";
import MainLayout from "../../components/layout/MainLayout";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { CircularProgress } from "@mui/material";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const DrawerHeader = styled("div")(({ theme }) => ({
    ...theme.mixins.toolbar,
}));

const Anonymized = () => {
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState("");
    const [downloadUrl, setDownloadUrl] = useState(null);
    const navigate = useNavigate();

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            setSelectedFile(acceptedFiles[0]);
            setMessage("");
            setDownloadUrl(null);
        }
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { "application/pdf": [] },
        multiple: true,
    });

    const handleAnonymize = async () => {
        if (!selectedFile) {
            setMessage("Please select a PDF file first.");
            return;
        }

        const formData = new FormData();
        formData.append("files", selectedFile);

        try {
            setUploading(true);
            const response = await axios.post(
                `${process.env.REACT_APP_PYTHON_BACKEND_URL}/anonymize`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.status === 200) {
                if (response.data.results[0]['download_url'] !== undefined) {
                    setMessage(`You have successfully generated an anonymized PDF.`);
                    setDownloadUrl(response.data.results[0]['download_url']);
                    setOpenUploadModal(false); // Close modal on success
                } else {
                    setMessage(`${response.data.results[0]['error']}`);
                }
            }
        } catch (error) {
            setMessage("Upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const downloadPDF = async () => {
        let fileUrl = `${downloadUrl}`;
        fileUrl = fileUrl.replace("http://", "https://");

        try {
            const response = await fetch(fileUrl, { redirect: "follow" });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const blob = await response.blob();
            const downloadUrlBlob = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = downloadUrlBlob;
            link.download = `anonymized_${selectedFile.name}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrlBlob);
        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    const handlePreview = async () => {
        let fileUrl = `${downloadUrl}`;
        fileUrl = fileUrl.replace("http://", "https://");
        window.open(fileUrl, '_blank');
    };

    return (
        <div className="h-[var(--app-height)] overflow-hidden">
            <Box className="flex h-full">
                {/* Sidebar */}
                <MainLayout />

                {/* Main content */}
                <Box component="main" className="flex-1 bg-white px-6 py-4 overflow-hidden">
                    <DrawerHeader />

                    {/* Header */}
                    <div className="flex items-start justify-between border-b border-gray-200 pb-4">
                        {/* Left */}
                        <div>
                            <div className="flex items-center gap-2">
                                <div className="bg-[#C800DE] p-[4px] mt-[-9px] inline-flex items-center justify-center cursor-pointer rounded-full">
                                    <ArrowBackIcon sx={{ fontSize: "8px", color: "#fff" }} />
                                </div>
                                <h1 className="text-lg font-semibold text-[#656565]">
                                    Anonymizer
                                </h1>
                            </div>
                        </div>

                        {/* Right buttons */}
                        <div className="flex items-center gap-2">
                            <button
                                disabled={downloadUrl === null}
                                onClick={handlePreview}
                                className={`flex items-center gap-2 px-2 py-2 text-sm border rounded-[12px] ${
                                    downloadUrl === null
                                        ? "border-[#818181] text-[#818181] cursor-not-allowed opacity-50"
                                        : "border-[#bb1ccc] text-[#bb1ccc] cursor-pointer hover:bg-[#bb1ccc] hover:text-white"
                                }`}
                            >
                                <VisibilityOutlinedIcon sx={{ fontSize: "14px" }} />
                                Preview
                            </button>

                            <button
                                disabled
                                className="flex items-center gap-2 px-2 text-[12px] py-2 text-sm border border-[#818181] rounded-[12px] text-[#818181] cursor-not-allowed opacity-50"
                            >
                                <ShareOutlinedIcon sx={{ fontSize: "14px" }} />
                                Share
                            </button>

                            <button
                                disabled={downloadUrl === null}
                                onClick={downloadPDF}
                                className={`flex items-center gap-2 px-2 py-2 text-sm border rounded-[12px] ${
                                    downloadUrl === null
                                        ? "border-[#818181] text-[#818181] cursor-not-allowed opacity-50"
                                        : "border-[#bb1ccc] text-[#bb1ccc] cursor-pointer hover:bg-[#bb1ccc] hover:text-white"
                                }`}
                            >
                                <FileDownloadOutlinedIcon sx={{ fontSize: "14px" }} />
                                Export
                            </button>

                            <button
                                onClick={() => setOpenUploadModal(true)}
                                className="flex items-center gap-2 px-2 py-2 text-sm font-medium text-white bg-[#bb1ccc] rounded-lg hover:bg-[#a91bb5]"
                            >
                                <UploadFileIcon sx={{ fontSize: "14px" }} />
                                Upload PDF
                            </button>
                        </div>
                    </div>
                                        {downloadUrl && (
                        <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-white">
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                    <div className="mt-1">
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <circle cx="12" cy="12" r="10" fill="#10B981" />
                                            <path
                                                d="M9 12L11 14L15 10"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-base font-semibold text-gray-900">
                                            Analysis Complete
                                        </h3>
                                        <p className="text-sm text-gray-600 mt-1">
                                            All compliance checks passed with minor recommendations
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-md">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <span className="text-xs font-medium text-green-700">
                                        Processing Complete
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                </Box>
            </Box>

            <Dialog
                open={openUploadModal}
                onClose={() => {
                    setOpenUploadModal(false);
                    setSelectedFile(null);
                    setMessage("");
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        mt: 10,
                        ml: 20,
                    },
                }}
            >
                <DialogContent>
                    <div className="p-4">
                        <h2 className="text-lg font-semibold text-[#1a1a1a]">
                            Upload PDF, DOC
                        </h2>

                        <div
                            {...getRootProps()}
                            className="mt-6 flex flex-col items-center justify-center
                                       border-2 border-dashed border-gray-300 rounded-xl
                                       h-[140px] cursor-pointer text-gray-500"
                        >
                            <input {...getInputProps()} />
                            <span>
                                {selectedFile ? "File selected" : "Drop or Upload your PDF here"}
                            </span>
                        </div>

                        {selectedFile && (
                            <p className="mt-2 text-sm text-[#bb1ccc]">
                                File Name:{" "}
                                <span className="font-medium">{selectedFile.name}</span>
                            </p>
                        )}

                    
                    </div>
                </DialogContent>

                <DialogActions className="px-6 pb-4">
                    <button
                        onClick={() => {
                            setOpenUploadModal(false);
                            setSelectedFile(null);
                            setMessage("");
                        }}
                        className="px-6 py-2 border border-gray-300 rounded-lg text-sm"
                        disabled={uploading}
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleAnonymize}
                        disabled={uploading || !selectedFile}
                        className="px-6 py-2 bg-[#bb1ccc] text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {uploading ? (
                            <CircularProgress size={20} sx={{ color: "white" }} />
                        ) : (
                            "Anonymize"
                        )}
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Anonymized;